import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ExamQuestionCard, { sameAnswer } from '../components/ExamQuestionCard';
import { fetchQuestionBank, BankData, BankQuestion } from '../utils/questionBankApi';
import { getExamById, getSessionExamIds } from '../data/examCatalog';

const EXAM_ID = 2; // AWS SAP-C02
const PER_PAGE = 20;
type Mode = 'practice' | 'study';
type Status = 'all' | 'todo' | 'wrong' | 'right' | 'mark';
interface Prog { s?: number[]; r?: boolean; m?: boolean; show?: boolean }

const field: React.CSSProperties = { padding: '9px 12px', border: '1.5px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.9rem', background: '#fff', minWidth: 0, flex: '1 1 150px' };

const QuestionBankPage: React.FC = () => {
  const navigate = useNavigate();
  const exam = getExamById(EXAM_ID);
  const user = sessionStorage.getItem('dashUser') || 'guest';
  const storeKey = `qbank_${EXAM_ID}_${user}`;

  const [data, setData] = useState<BankData | null>(null);
  const [error, setError] = useState('');
  const [prog, setProg] = useState<Record<number, Prog>>(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || '{}'); } catch { return {}; }
  });
  const [sel, setSel] = useState<Record<number, number[]>>({});
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const [setFilter, setSetFilter] = useState('');
  const [mode, setMode] = useState<Mode>('practice');
  const [status, setStatus] = useState<Status>('all');
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [page, setPage] = useState(0);

  // Only learners assigned to this exam may open the page
  useEffect(() => {
    if (!getSessionExamIds().includes(EXAM_ID)) navigate('/dashboard', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    fetchQuestionBank(EXAM_ID).then(res => {
      if (cancelled) return;
      if (res.success && res.data) setData(res.data); else setError(res.error || 'Could not load the question bank.');
    });
    return () => { cancelled = true; };
  }, []);

  const saveProg = (next: Record<number, Prog>) => {
    setProg(next);
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };
  const patch = (id: number, p: Partial<Prog>, drop: (keyof Prog)[] = []) => {
    const cur = { ...(prog[id] || {}), ...p };
    drop.forEach(k => delete cur[k]);
    saveProg({ ...prog, [id]: cur });
  };

  const categories = useMemo(() => (data ? Array.from(new Set(data.questions.map(q => q.category))).sort() : []), [data]);

  const list = useMemo(() => {
    if (!data) return [] as BankQuestion[];
    const s = search.trim().toLowerCase();
    let out = data.questions.filter(q => {
      const g = prog[q.id] || {};
      if (cat && q.category !== cat) return false;
      if (setFilter && String(q.setNo) !== setFilter) return false;
      if (s && !(q.q + ' ' + q.opts.join(' ')).toLowerCase().includes(s)) return false;
      if (status === 'todo' && g.r !== undefined) return false;
      if (status === 'wrong' && g.r !== false) return false;
      if (status === 'right' && g.r !== true) return false;
      if (status === 'mark' && !g.m) return false;
      return true;
    });
    if (shuffleSeed) {
      out = [...out];
      let seed = shuffleSeed;
      const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
      for (let i = out.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [out[i], out[j]] = [out[j], out[i]]; }
    }
    return out;
  }, [data, prog, search, cat, setFilter, status, shuffleSeed]);

  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const cur = Math.min(page, pages - 1);
  const shown = list.slice(cur * PER_PAGE, cur * PER_PAGE + PER_PAGE);
  const attempted = Object.values(prog).filter(g => g.r !== undefined);
  const correct = attempted.filter(g => g.r).length;

  const toggle = (q: BankQuestion) => (_: number, opt: number) => {
    const have = sel[q.id] || [];
    const need = q.ans.length || 1;
    const next = need === 1 ? [opt] : have.includes(opt) ? have.filter(x => x !== opt) : [...have, opt].slice(-need);
    setSel({ ...sel, [q.id]: next });
  };
  const check = (q: BankQuestion) => () => {
    const picked = sel[q.id] || [];
    patch(q.id, { s: picked, r: sameAnswer(picked, q.ans) });
  };
  const tryAgain = (q: BankQuestion) => { patch(q.id, {}, ['r', 's', 'show']); const n = { ...sel }; delete n[q.id]; setSel(n); };

  const bar = (n: number, title: string) => (
    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 6px 2px' }}>{title} #{n}</div>
  );

  return (
    <Layout>
      <div style={{ background: 'linear-gradient(135deg, var(--blue-pale), var(--orange-pale))', borderBottom: '1px solid var(--gray-200)', padding: '26px 5%', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>📘 {exam?.short} <span style={{ color: 'var(--blue)' }}>Question Bank</span></h1>
        <p style={{ color: 'var(--gray-600)', fontSize: '0.92rem', marginTop: '4px' }}>
          {data ? `${data.questions.length} questions in ${data.sets.filter(s => s.unlocked).length} of ${data.sets.length} sets` : 'Loading…'}
        </p>
      </div>

      <div style={{ background: '#fff', borderBottom: '1px solid var(--gray-200)', padding: '12px 5%' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <input type="search" style={{ ...field, flex: '1 1 100%' }} placeholder="Search questions…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
          <select style={field} value={cat} onChange={e => { setCat(e.target.value); setPage(0); }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
          <select style={field} value={setFilter} onChange={e => { setSetFilter(e.target.value); setPage(0); }}>
            <option value="">All sets</option>
            {data?.sets.map(s => <option key={s.set} value={s.set} disabled={!s.unlocked}>{s.unlocked ? `Set ${s.set}` : `🔒 Set ${s.set}`}</option>)}
          </select>
          <select style={field} value={mode} onChange={e => setMode(e.target.value as Mode)}>
            <option value="practice">Practice (check answer)</option>
            <option value="study">Study (show answers)</option>
          </select>
          <select style={field} value={status} onChange={e => { setStatus(e.target.value as Status); setPage(0); }}>
            <option value="all">All</option><option value="todo">Not attempted</option><option value="wrong">Wrong</option><option value="right">Correct</option><option value="mark">Bookmarked</option>
          </select>
          <button className="btn btn-outline-blue btn-sm" onClick={() => { setShuffleSeed(Math.floor(Math.random() * 100000) + 1); setPage(0); }}>🔀 Shuffle</button>
          <button className="btn btn-outline-gray btn-sm" onClick={() => { if (window.confirm('Clear all saved answers and bookmarks?')) { saveProg({}); setSel({}); } }}>Reset progress</button>
        </div>
      </div>

      <div style={{ maxWidth: '1000px', margin: '20px auto', padding: '0 5% 40px' }}>
        {error && <p style={{ color: 'var(--red)' }}>⚠️ {error}</p>}
        {!data && !error && <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '40px' }}>Loading questions…</p>}

        {data && data.sets.some(s => !s.unlocked) && (
          <div style={{ background: 'var(--orange-pale)', border: '1px solid var(--orange-light)', borderRadius: '10px', padding: '10px 14px', fontSize: '0.85rem', marginBottom: '14px' }}>
            🔒 Your plan includes {data.sets.filter(s => s.unlocked).length} of {data.sets.length} sets. The other sets are locked.
          </div>
        )}

        {data && (
          <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '12px' }}>
            {list.length} questions shown · attempted {attempted.length}/{data.questions.length} · correct {correct}{attempted.length ? ` (${Math.round(correct / attempted.length * 100)}%)` : ''}
          </p>
        )}
        {data && list.length === 0 && <p style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '30px' }}>No questions match.</p>}

        {shown.map((q, i) => {
          const g = prog[q.id] || {};
          const noKey = q.ans.length === 0;
          const study = mode === 'study';
          const checked = !noKey && (g.r !== undefined || study);
          const picked = g.r !== undefined ? (g.s || []) : (sel[q.id] || []);
          return (
            <div key={q.id} style={{ marginBottom: '18px' }}>
              {bar(q.sourceNo ?? cur * PER_PAGE + i + 1, `Set ${q.setNo} · ${q.category} ·`)}
              <ExamQuestionCard
                question={q}
                qIdx={cur * PER_PAGE + i}
                selected={picked}
                isChecked={checked}
                isPracticeMode={!noKey && !study}
                onToggle={toggle(q)}
                onCheck={check(q)}
              />
              {noKey && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Answer key not available for this question.</div>
                  {(g.show || study) && q.explain
                    ? <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem', background: 'var(--blue-pale)', border: '1px solid var(--blue-soft)', borderRadius: 'var(--radius)', padding: '14px', marginTop: '8px' }}>{q.explain}</pre>
                    : q.explain && <button className="btn btn-outline-blue btn-sm" style={{ marginTop: '6px' }} onClick={() => patch(q.id, { show: true })}>Show explanation</button>}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {g.r !== undefined && !study && <button className="btn btn-outline-gray btn-sm" onClick={() => tryAgain(q)}>Try again</button>}
                <button className="btn btn-outline-gray btn-sm" onClick={() => patch(q.id, { m: !g.m })}>{g.m ? '★ Bookmarked' : '☆ Bookmark'}</button>
              </div>
            </div>
          );
        })}

        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
            <button className="btn btn-outline-blue btn-sm" disabled={cur === 0} onClick={() => { setPage(cur - 1); window.scrollTo(0, 0); }}>← Prev</button>
            <span style={{ fontWeight: 600 }}>Page {cur + 1} / {pages}</span>
            <button className="btn btn-outline-blue btn-sm" disabled={cur >= pages - 1} onClick={() => { setPage(cur + 1); window.scrollTo(0, 0); }}>Next →</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QuestionBankPage;
